import { type AABB3, type View } from "@novorender/api";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { FeatureToggle } from "config/featureToggles";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useHighlighted } from "contexts/highlighted";
import { selectMainObject, selectTopLevelSelections } from "features/render/renderSlice";
import { persistFeatureToggle } from "slices/featureToggles";
import { sceneVolumeAabb } from "utils/math";
import { getSelectionBoundingBox, resolveTopLevelBoundingBox } from "utils/objectData";
import { type SelectionSet } from "utils/selectionSet";

import { clearDroneOnNextConnect, consumeDroneOnNextConnect } from "./droneOnConnect";
import { clearSpaceMouseDriverConfirmed, setSpaceMouseStatus, type SpaceMouseFailure } from "./status";
import { useLoopbackPermissionState } from "./useLoopbackPermission";
import { useSpaceMouseAccess } from "./useSpaceMouseAccess";

/**
 * How long to wait for a working session before giving up. With no driver the SDK reports nothing, so
 * this timeout is the only thing that detects that case. Overshooting is invisible.
 */
const HANDSHAKE_TIMEOUT_MS = 6000;

/**
 * Reconnect budget for one incident (starts when a working session drops). Sized to outlast a 3DxWare
 * restart, but bounded so a driver that never returns ends in a failure note.
 */
const MAX_RECONNECTS = 6;
const RECONNECT_INTERVAL_MS = 5000;

/**
 * How long a session must hold up to count as recovered and restore the budget. Time-based, or a driver
 * that dies as fast as it connects would earn a fresh budget every cycle and reconnect forever.
 */
const STABLE_SESSION_MS = 20000;

/**
 * Object-DB `getBounds` reads a row per id, so folding stops here (BM-10168). Past it, Fit reads
 * the tree's own node bounds instead. `useFlyToSelected` uses the same 5000 for its tree-node
 * path, inclusive (`>=`); Fit stays exclusive so a 5000-id selection still folds.
 */
const MAX_IDS_TO_FOLD = 5000;

/**
 * The tree map is only a shortcut while it stands for far fewer nodes than the selection has ids.
 * A file pick is one node over hundreds of thousands of ids; a box-select is closer to one node
 * per id, where walking the nodes costs as much as folding them.
 */
const MAX_TOP_LEVEL_NODES = 32;
const MIN_IDS_PER_TOP_LEVEL_NODE = 50;

/** Stop counting once the map cannot compress: more than 32 nodes and more than one per 50 ids. */
function isCompressedTopLevel(topLevelSelections: Record<number, { path: string }>, idCount: number): boolean {
    const maxUncompressed = Math.max(MAX_TOP_LEVEL_NODES, Math.floor(idCount / MIN_IDS_PER_TOP_LEVEL_NODE));
    let n = 0;
    for (const _ in topLevelSelections) {
        n++;
        if (n > maxUncompressed) {
            return false;
        }
    }
    return n > 0 && (n <= MAX_TOP_LEVEL_NODES || n * MIN_IDS_PER_TOP_LEVEL_NODE < idCount);
}

type AABB6 = readonly [number, number, number, number, number, number];

function aabb3ToExtents(box: AABB3): AABB6 {
    return [box.min[0], box.min[1], box.min[2], box.max[0], box.max[1], box.max[2]];
}

/** Same volume the driver already uses for the whole model when there is no selection. */
function sceneAabb(view: View): AABB6 | null {
    const box = sceneVolumeAabb(view.renderState.scene?.config);
    return box ? aabb3ToExtents(box) : null;
}

type SelectionPush = {
    extents: AABB6 | null;
    objectIds?: SelectionSet;
};

type LiveConnection = {
    stop: () => void;
    setSelectionExtents: (e: AABB6 | null, objectIds?: SelectionSet) => void;
    notifyCameraMoved: () => void;
    notifyCameraSettled: () => void;
};

/**
 * Wires up 3Dconnexion SpaceMouse navigation while the viewer is mounted.
 *
 * Opt-in via the SpaceMouse setting, itself behind the host's rollout flag; only then does it connect
 * to the local driver (once view + canvas are ready), so the loopback handshake and its permission
 * prompt stay inert by default. The session also requires Default nav mode (`flight`) and a desktop
 * viewport: Orbit, Walk, Procore iOS/Android, and phone-sized layouts tear it down even if the setting
 * stays on. The flag is re-checked here because the setting persists to localStorage, so a user who
 * enabled it before the flag was turned off could otherwise never stop.
 *
 * `./connection` (and its SDK, which writes browser globals at module-eval) is loaded via dynamic
 * `import()` so it never lands in the eager chunk. Keep it lazy.
 */
export function useSpaceMouse(): void {
    const dispatch = useAppDispatch();
    const { sessionEnabled: enabled, settingOn, desktopAvailable } = useSpaceMouseAccess();
    const permission = useLoopbackPermissionState();
    const permissionBlocked = permission === "denied" || permission === "pending";
    const {
        state: { view, canvas, dataAccess, internalEventEmitter },
    } = useExplorerGlobals();
    const highlighted = useHighlighted();
    const mainObject = useAppSelector(selectMainObject);
    const topLevelSelections = useAppSelector(selectTopLevelSelections);
    const lastIdsRef = useRef(highlighted.ids);
    const lastTopLevelRef = useRef(topLevelSelections);
    const connectionRef = useRef<LiveConnection | null>(null);
    const lastSelectionRef = useRef<SelectionPush>({ extents: null });

    const pushSelection = (connection: LiveConnection | null): void => {
        const { extents, objectIds } = lastSelectionRef.current;
        connection?.setSelectionExtents(extents, objectIds);
    };

    // Chrome won't re-prompt a blocked loopback permission, so a denied state can never connect. Turn
    // the setting back off (the Settings note tells the user how to re-grant it in browser settings).
    // Gated on the setting + desktop, not the live session: denying while in Orbit/Walk must still
    // clear the pending Drone write instead of waiting until the user returns to Default.
    useEffect(() => {
        if (settingOn && desktopAvailable && permission === "denied") {
            clearDroneOnNextConnect();
            dispatch(persistFeatureToggle(FeatureToggle.SpaceMouseEnabled, false));
        }
    }, [settingOn, desktopAvailable, permission, dispatch]);

    // The driver may be gone by the time the user turns it back on, so don't inherit the confirmation.
    useEffect(() => {
        if (!settingOn) {
            clearSpaceMouseDriverConfirmed();
        }
    }, [settingOn]);

    useEffect(() => {
        // Wait for the permission query to resolve before connecting so we never attempt a handshake we
        // already know is blocked. "unsupported" (non-Chromium/older) still connects and degrades on failure.
        if (!enabled || permissionBlocked || !view || !canvas) {
            setSpaceMouseStatus("idle");
            return;
        }

        // Guard against the effect tearing down before an async step resolves.
        let disposed = false;
        let connection: LiveConnection | null = null;
        let handshakeTimer: number | null = null;
        let reconnectTimer: number | null = null;
        let stableTimer: number | null = null;
        // Reconnects spent on the current incident; non-zero means one is open.
        let reconnects = 0;

        setSpaceMouseStatus("connecting");

        const clearTimer = (timer: number | null): null => {
            if (timer !== null) {
                window.clearTimeout(timer);
            }
            return null;
        };

        const fail = (failure: SpaceMouseFailure): void => {
            if (disposed) {
                return;
            }
            handshakeTimer = clearTimer(handshakeTimer);
            stableTimer = clearTimer(stableTimer);
            // One attempt can fail twice (handshake timeout, then a late close); clear so the second
            // doesn't strangle the retry the first armed.
            reconnectTimer = clearTimer(reconnectTimer);

            // Any failure keeps an open incident going, not just a drop: a driver mid-restart refuses
            // the socket, so attempts after the first are "unreachable" and would otherwise dead-end.
            if ((failure === "lost" || reconnects > 0) && reconnects < MAX_RECONNECTS) {
                reconnects++;
                // The session really is down for the length of the wait.
                setSpaceMouseStatus("connecting");
                reconnectTimer = window.setTimeout(attemptConnection, RECONNECT_INTERVAL_MS);
                return;
            }
            // Reported even if the permission prompt is still open (`./note` decides what's shown).
            // Suppressing it here left the status stuck on "connecting", hiding a missing driver.
            setSpaceMouseStatus("unavailable");
        };

        // An arrow, not a hoisted declaration, so TS keeps the `view` narrowing from the guard above.
        const attemptConnection = (): void => {
            void (async () => {
                connection?.stop();
                const { SpaceMouseConnection } = await import("./connection");
                if (disposed) {
                    return;
                }
                const opened = new SpaceMouseConnection({
                    getView: () => view,
                    shouldApplyDrone: consumeDroneOnNextConnect,
                    onConnected: () => {
                        handshakeTimer = clearTimer(handshakeTimer);
                        // A timed-out attempt can still come good; cancel the queued replacement so it
                        // doesn't stop the session that just started working.
                        reconnectTimer = clearTimer(reconnectTimer);
                        // Can fire more than once (SDK reconnect); drop any orphaned stable timer.
                        stableTimer = clearTimer(stableTimer);
                        setSpaceMouseStatus("connected");
                        // `reconnects` is zeroed only by surviving STABLE_SESSION_MS, not here.
                        stableTimer = window.setTimeout(() => {
                            reconnects = 0;
                        }, STABLE_SESSION_MS);
                        // Fresh handle after Default→Orbit→Default or a reconnect; extents live in the
                        // per-connection closure and would otherwise stay null until the next select.
                        pushSelection(opened);
                    },
                    onFailed: fail,
                });
                connection = opened;
                connectionRef.current = opened;
                pushSelection(opened);
                // Armed before start(), which can fail synchronously and clear this timer; arming after
                // would leave one behind to fire over an already-reported failure.
                handshakeTimer = window.setTimeout(() => fail("unreachable"), HANDSHAKE_TIMEOUT_MS);
                opened.start();
            })().catch(() => {
                // Only reachable if the dynamic import fails (chunk load error).
                fail("unreachable");
            });
        };

        attemptConnection();

        return () => {
            disposed = true;
            handshakeTimer = clearTimer(handshakeTimer);
            reconnectTimer = clearTimer(reconnectTimer);
            stableTimer = clearTimer(stableTimer);
            connection?.stop();
            connectionRef.current = null;
            setSpaceMouseStatus("idle");
        };
        // prompt → granted must not re-run: that remount would stop a session that already wrote Drone.
        // Toggling the setting off and on is the manual retry: it changes `enabled` and re-runs this.
    }, [enabled, permissionBlocked, view, canvas]);

    // Mouse/WASD never go through the SpaceMouse driver. cameraUpdated reprojects Always Show;
    // cameraSettled fence-syncs our look-at cache and writes model.extents so the driver recalculates
    // auto-pivot (otherwise the next SpaceMouse gesture keeps the pre-mouse latch).
    useEffect(() => {
        if (!enabled || !internalEventEmitter) {
            return;
        }
        const unsubMoved = internalEventEmitter.on("cameraUpdated", () => {
            connectionRef.current?.notifyCameraMoved();
        });
        const unsubSettled = internalEventEmitter.on("cameraSettled", () => {
            connectionRef.current?.notifyCameraSettled();
        });
        return () => {
            unsubMoved();
            unsubSettled();
        };
    }, [enabled, internalEventEmitter]);

    // Feed the selection so the driver can apply Auto on the whole scene or, with "Use selected
    // item", only inside the selection (volume when framed, surface near view center when zoomed in).
    // Gated on `enabled` so users without SpaceMouse do not pay a metadata search on every select.
    useEffect(() => {
        if (!enabled || !view || !dataAccess) {
            lastSelectionRef.current = { extents: null };
            return;
        }
        const allIds = mainObject === undefined ? highlighted.ids : highlighted.ids.withSingleAdded(mainObject);
        if (allIds.size === 0) {
            lastSelectionRef.current = { extents: null };
            connectionRef.current?.setSelectionExtents(null);
            return;
        }
        const abort = new AbortController();
        const push = (extents: AABB6 | null): void => {
            if (abort.signal.aborted) {
                return;
            }
            lastSelectionRef.current = { extents, objectIds: allIds };
            connectionRef.current?.setSelectionExtents(extents, allIds);
        };

        // `useTopLevelSelectionsSync` writes Redux in an effect, so the first render after a
        // selection change still has the previous nodes. Skip them until the map identity updates.
        const idsJustChanged = lastIdsRef.current !== highlighted.ids;
        const topLevelUnchanged = lastTopLevelRef.current === topLevelSelections;
        lastIdsRef.current = highlighted.ids;
        lastTopLevelRef.current = topLevelSelections;
        const topLevelMayBeStale = idsJustChanged && topLevelUnchanged;

        void (async () => {
            const tooManyToFold = allIds.size > MAX_IDS_TO_FOLD;
            const tryTopLevel =
                tooManyToFold && isCompressedTopLevel(topLevelSelections, allIds.size) && !topLevelMayBeStale;

            if (tooManyToFold) {
                // Until the node/fold answers, Fit must not keep the previous object.
                push(sceneAabb(view));
            }
            if (tryTopLevel) {
                let nodeBox;
                try {
                    nodeBox = await resolveTopLevelBoundingBox({
                        topLevelSelections,
                        dataAccess,
                        abortSignal: abort.signal,
                    });
                } catch {
                    nodeBox = undefined;
                }
                if (abort.signal.aborted) {
                    return;
                }
                if (nodeBox) {
                    push(aabb3ToExtents(nodeBox));
                    return;
                }
            }
            if (tooManyToFold) {
                return;
            }

            try {
                const box = await getSelectionBoundingBox({
                    ids: allIds.sortedIds,
                    abortSignal: abort.signal,
                    dataAccess,
                });
                // The box, not the sphere: boxing a sphere stretches every axis to the diagonal.
                push(box ? aabb3ToExtents(box) : null);
            } catch {
                push(null);
            }
        })();

        return () => abort.abort();
    }, [enabled, mainObject, view, dataAccess, highlighted.ids, topLevelSelections]);
}
